import { GetPlaceCodesAndSortsQuery, PlaceCodeSortConfigDisabledTargetInput } from '../generated/graphql';
import {
  DEFAULT_AREA_DISPLAY_UNIT,
  formatEverguardThresholdLabel,
} from '../utils/PlaceCodeDisplayUnits';

export type CodeSortPlace = GetPlaceCodesAndSortsQuery['getPlace'];
export type CodeSortEverguardSetting = { id: number, everguard_threshold: number };
export type CodeSortCode = {
  id: number | null,
  name: string,
  area_display_unit: number,
  everguard_setting?: CodeSortEverguardSetting | null
};
export type CodeSortSort = { id: number | null, name: string };
export type CodeSortConfig = CodeSortPlace['code_sort_configs'][number];
export type CodeSortRow = {
  code: CodeSortCode,
  sort: CodeSortSort,
  config: CodeSortConfig | undefined,
  gteEverguardThreshold: boolean
};

export function hasEverguardPair(code: CodeSortCode): code is CodeSortCode & { everguard_setting: CodeSortEverguardSetting } {
  if (code.everguard_setting && code.everguard_setting.id) {
    return true;
  }
  return false;
}

function toThresholdLabel(gte: boolean): string {
  if (gte) {
    return '以上';
  }
  return '未満';
}

function toIdKeyPart(id: number | null): string {
  if (id === null) {
    return 'none';
  }
  return `${id}`;
}

/**
 * 分類と補修コードの組み合わせの行を並べる。
 * 無効にした組み合わせも並び順を変えずにその場所に出す。
 * 剥落防止の設定がある補修コードは、浮き面積の基準以上と未満の2行に分け、基準の値は補修コードの面積の表示単位で書く。
 */
export function buildCodeAndSortRows(place: CodeSortPlace): CodeSortRow[] {
  const codes = [...place.codes] as CodeSortCode[];
  codes.push({
    id: null,
    name: '指定なし',
    area_display_unit: DEFAULT_AREA_DISPLAY_UNIT,
  });

  const sorts = [...place.sorts] as CodeSortSort[];
  sorts.push({ id: null, name: '指定なし' });

  const rows: CodeSortRow[] = [];

  for (const sort of sorts) {
    for (const code of codes) {
      const codeEverguardId = code.everguard_setting?.id ?? null;
      const matchedConfigs = place.code_sort_configs
        .filter(config => config.code_id === code.id && config.sort_id === sort.id && config.place_code_everguard_id === codeEverguardId);

      if (hasEverguardPair(code)) {
        const thresholdLabel = formatEverguardThresholdLabel(
          code.everguard_setting.everguard_threshold,
          code.area_display_unit
        );
        for (const gteThreshold of [true, false]) {
          const config = matchedConfigs.find(matchedConfig => matchedConfig.gte_everguard_threshold === gteThreshold);
          const codeName = `${code.name}（${thresholdLabel} ${toThresholdLabel(gteThreshold)}）`;
          rows.push({ code: { ...code, name: codeName }, sort, config, gteEverguardThreshold: gteThreshold });
        }
      } else {
        rows.push({ code, sort, config: matchedConfigs[0], gteEverguardThreshold: false });
      }
    }
  }

  return rows;
}

export function toRowKey(row: CodeSortRow): string {
  return `${toIdKeyPart(row.code.id)}:${toIdKeyPart(row.sort.id)}:${row.gteEverguardThreshold}`;
}

export function toSelectionKeys(row: CodeSortRow): string[] {
  if (hasEverguardPair(row.code)) {
    const pairRow: CodeSortRow = { ...row, gteEverguardThreshold: !row.gteEverguardThreshold };
    return [toRowKey(row), toRowKey(pairRow)];
  }
  return [toRowKey(row)];
}

export function toggleRowSelection(selectedKeys: string[], row: CodeSortRow): string[] {
  const keys = toSelectionKeys(row);
  const isRowSelected = selectedKeys.includes(toRowKey(row));

  if (isRowSelected) {
    return selectedKeys.filter(selectedKey => !keys.includes(selectedKey));
  }
  const addedKeys = keys.filter(key => !selectedKeys.includes(key));
  return selectedKeys.concat(addedKeys);
}

export function isAllSelected(selectedKeys: string[], rows: CodeSortRow[]): boolean {
  if (rows.length > 0) {
    return rows.every(row => selectedKeys.includes(toRowKey(row)));
  }
  return false;
}

export function toggleAllSelection(selectedKeys: string[], rows: CodeSortRow[]): string[] {
  if (isAllSelected(selectedKeys, rows)) {
    return [];
  }
  return rows.map(row => toRowKey(row));
}

export function toDisabledTargets(selectedKeys: string[], rows: CodeSortRow[]): PlaceCodeSortConfigDisabledTargetInput[] {
  const selectedRows = rows.filter(row => selectedKeys.includes(toRowKey(row)));
  return selectedRows.map(row => ({
    code_id: row.code.id,
    sort_id: row.sort.id,
    place_code_everguard_id: row.code.everguard_setting?.id ?? null,
    gte_everguard_threshold: row.gteEverguardThreshold
  }));
}
